<template>
  <div class="cq-page py-3">
    <header class="cq-toolbar">
      <div class="cq-toolbar__title">
        <span class="cq-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>My Call Queue</h1>
          <p>
            Hi <strong>{{ greetingName }}</strong> — here's what's lined up for you today.
            <span v-if="orgLabel" class="cq-toolbar__org">{{ orgLabel }}</span>
          </p>
        </div>
      </div>
      <div class="cq-toolbar__actions">
        <span v-if="lastFetchedAt && !isLoading" class="cq-updated">
          Updated {{ lastFetchedLabel }}
        </span>
        <CButton size="sm" class="refresh-btn" :class="{ 'is-loading': isBusy }" :disabled="isBusy" @click="refresh">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ isBusy ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="cq-alert">{{ errorMessage }}</CAlert>

    <div v-if="!supportedOrg && !isLoading" class="cq-unsupported">
      <div class="cq-unsupported__title">Call Queue not available for this organisation yet</div>
      <div class="cq-unsupported__hint">Phase 1 only covers MAP, MDI, and MVP. Switch organisation in the sidebar if you have access elsewhere.</div>
    </div>

    <template v-if="supportedOrg">
      <!-- Summary tiles -->
      <div class="cq-summary">
        <div
          v-for="tab in tabsForRender"
          :key="`tile-${tab.key}`"
          class="cq-summary__tile"
          :class="{
            'is-active': tab.isParent ? (activeParentTab && activeParentTab.key === tab.key) : activeTab === tab.key,
            'is-empty': !tab.count
          }"
          role="button"
          tabindex="0"
          @click="selectTopLevelTab(tab)"
          @keyup.enter="selectTopLevelTab(tab)"
        >
          <span class="cq-summary__icon" :style="{ background: tab.tint }" aria-hidden="true" v-html="tab.icon"></span>
          <span class="cq-summary__body">
            <span class="cq-summary__label">{{ tab.label }}</span>
            <span class="cq-summary__count">{{ tab.count }}</span>
          </span>
        </div>
      </div>

      <!-- Tabs (scrollable on mobile) -->
      <nav class="cq-tabs" aria-label="Call queue sections">
        <button
          v-for="tab in tabsForRender"
          :key="`tab-${tab.key}`"
          type="button"
          class="cq-tab"
          :class="{ 'is-active': tab.isParent ? (activeParentTab && activeParentTab.key === tab.key) : activeTab === tab.key }"
          @click="selectTopLevelTab(tab)"
        >
          <span class="cq-tab__name">{{ tab.label }}</span>
          <span class="cq-tab__count" :class="{ 'is-zero': !tab.count }">{{ tab.count }}</span>
        </button>
      </nav>

      <!-- Inner sub-tabs: appear only when the active leaf is the child of
           a parent group (e.g. MVP leads → Followups / Feedbacks). -->
      <nav v-if="activeParentTab" class="cq-subtabs" :aria-label="`${activeParentTab.label} sub-sections`">
        <button
          v-for="child in childTabsForRender"
          :key="`subtab-${child.key}`"
          type="button"
          class="cq-subtab"
          :class="{ 'is-active': activeTab === child.key }"
          @click="activeTab = child.key"
        >
          <span class="cq-subtab__name">{{ child.label }}</span>
          <span class="cq-subtab__count" :class="{ 'is-zero': !child.count }">{{ child.count }}</span>
        </button>
      </nav>

      <!-- Active tab content -->
      <section class="cq-list-card">
        <header class="cq-list-card__head">
          <div>
            <h2>{{ activeTabMeta.label }}</h2>
            <p v-if="activeTabMeta.description" class="cq-list-card__desc">{{ activeTabMeta.description }}</p>
          </div>
          <div class="cq-list-card__tools">
            <div class="cq-search-wrap">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="cq-search-icon" aria-hidden="true">
                <circle cx="11" cy="11" r="7"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                v-model.trim="searchTerm"
                type="text"
                class="cq-search-input"
                :placeholder="`Search ${activeTabMeta.label.toLowerCase()}…`"
              />
              <button v-if="searchTerm" type="button" class="cq-search-clear" @click="searchTerm = ''" aria-label="Clear search">×</button>
            </div>
            <span v-if="lastGeneratedFor(activeTab)" class="cq-list-card__generated">
              Generated · {{ formatRelative(lastGeneratedFor(activeTab)) }}
            </span>
          </div>
        </header>

        <div v-if="isLoading" class="cq-list-state">
          <CSpinner size="sm" class="mr-2" />
          <span>Loading your queue…</span>
        </div>

        <div v-else-if="!filteredRows.length" class="cq-list-state cq-list-state--empty">
          <div class="cq-list-state__title">{{ searchTerm ? `No matches for "${searchTerm}"` : 'Nothing in this list right now.' }}</div>
          <div v-if="searchTerm" class="cq-list-state__hint">
            <button type="button" class="cq-link-btn" @click="searchTerm = ''">Clear search</button>
          </div>
          <div v-else class="cq-list-state__hint">When the cron runs again, fresh items will land here.</div>
        </div>

        <ul v-else class="cq-row-list">
          <li
            v-for="(row, index) in filteredRows"
            :key="`row-${activeTab}-${row._id || index}`"
            class="cq-row"
            :class="{ 'is-called': isPhoneCalled(row.phone) }"
          >
            <span class="cq-row__avatar" :style="avatarStyle(row.name || row.phone)">
              <span>{{ initials(row.name || row.phone) }}</span>
            </span>
            <div class="cq-row__main">
              <div class="cq-row__line">
                <span class="cq-row__name">{{ row.name || 'Unnamed contact' }}</span>
                <span v-if="row.badge" :class="['cq-row__badge', row.badgeClass]">{{ row.badge }}</span>
                <span v-if="isPhoneCalled(row.phone)" class="cq-row__called-tag">
                  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Called
                </span>
              </div>
              <div class="cq-row__line cq-row__line--meta">
                <template v-if="row.phone">
                  <a :href="`tel:${row.phone}`" class="cq-row__phone">{{ row.phone }}</a>
                  <button
                    type="button"
                    class="cq-copy-btn"
                    :class="{ 'is-copied': isPhoneCopied(row.phone) }"
                    :aria-label="isPhoneCopied(row.phone) ? 'Phone number copied' : 'Copy phone number'"
                    @click.stop.prevent="copyPhone(row.phone)"
                  >
                    <svg v-if="!isPhoneCopied(row.phone)" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    <svg v-else viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span>{{ isPhoneCopied(row.phone) ? 'Copied' : 'Copy' }}</span>
                  </button>
                </template>
                <template v-if="row.altPhone">
                  <a :href="`tel:${row.altPhone}`" class="cq-row__phone cq-row__phone--alt" title="Alternate number">
                    Alt {{ row.altPhone }}
                  </a>
                  <button
                    type="button"
                    class="cq-copy-btn"
                    :class="{ 'is-copied': isPhoneCopied(row.altPhone) }"
                    :aria-label="isPhoneCopied(row.altPhone) ? 'Alternate phone number copied' : 'Copy alternate phone number'"
                    @click.stop.prevent="copyPhone(row.altPhone)"
                  >
                    <svg v-if="!isPhoneCopied(row.altPhone)" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    <svg v-else viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span>{{ isPhoneCopied(row.altPhone) ? 'Copied' : 'Copy' }}</span>
                  </button>
                </template>
                <span v-if="row.subtitle" class="cq-row__subtitle">{{ row.subtitle }}</span>
              </div>
              <div v-if="row.detail" class="cq-row__detail">{{ row.detail }}</div>
            </div>
            <div class="cq-row__actions">
              <span v-if="row.amount" class="cq-row__amount">₹{{ formatNumber(row.amount) }}</span>
              <button
                type="button"
                class="cq-history-btn"
                :disabled="!row.phone"
                @click="openHistory(row)"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M3 3v5h5"/>
                  <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/>
                  <path d="M12 7v5l4 2"/>
                </svg>
                <span>View history</span>
              </button>
              <a
                v-if="isMobile && row.phone"
                :href="`tel:${row.phone}`"
                class="cq-call-btn cq-call-btn--link"
                @click="markCalled(row.phone)"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z"/>
                </svg>
                <span>Call</span>
              </a>
              <button
                v-else
                type="button"
                class="cq-call-btn"
                :disabled="!row.phone"
                @click="callContact(row)"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z"/>
                </svg>
                <span>Call</span>
              </button>
            </div>
          </li>
        </ul>
      </section>
    </template>

    <CustomerHistoryModal
      :show.sync="historyModalShow"
      :phone="historyPhone"
      :name="historyName"
    />
  </div>
</template>

<script>
import axios from 'axios'
import CustomerHistoryModal from '@/components/CustomerHistoryModal.vue'

const ICONS = {
  phone:    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z"/></svg>',
  flame:    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 17h2a2.5 2.5 0 0 0 0-5H11a2.5 2.5 0 0 1 0-5h.5A2.5 2.5 0 0 1 14 9.5"/></svg>',
  pool:     '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
  layers:   '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>',
  inbox:    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>',
  star:     '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>',
  spark:    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 0 1-9 9"/></svg>',
  refresh:  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>',
  target:   '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>'
}

const ENDPOINTS = {
  1: '/api/map-all-followups', // MAP
  2: '/api/mdi-all-followups', // MDI
  4: '/api/mvp-all-followups'  // MVP
}

const ORG_LABELS = {
  1: 'MAP · Manakkal Agro Pharma',
  2: 'MDI · Manakkal Drugs India',
  4: 'MVP · Manakkal Veterinary Pharma'
}

// Tab definitions per org. Each org gets its own ordered list (by
// importance from a tele-staff perspective). MDI's keys differ from MAP's
// (e.g., MDI has doctors_followups + priority_followups, MAP doesn't), so
// they don't share a TAB_DEFS list. Keep the keys in lockstep with the
// fields returned by the matching backend endpoint:
//   org 1 (MAP) → MapCallQueueCon::mapCallsAllForView
//   org 2 (MDI) → MdiCallQueue::mdiCallsAllForView
const TAB_DEFS_BY_ORG = {
  1: [
    { key: 'own_followups',        label: 'My follow-ups',     desc: 'Your own contacts due for the next call.', tint: '#ecfdf5', icon: ICONS.phone },
    { key: 'hp_followups',         label: 'High priority',     desc: 'Recent payment-pending / call-again-high entries.', tint: '#fef3c7', icon: ICONS.flame },
    // Parent grouping — clicking lands on the first non-empty child
    // (3+ mine by default, falls back to 1+ all when mine is empty).
    // Backend response shape: repeated_attempts_mine + repeated_attempts_all.
    { key: 'repeated_attempts', label: 'Repeated attempts',
      desc: 'Contacts dialled 3+ times that still haven\'t sold — push hard, escalate, or unlock a special offer.',
      tint: '#ffedd5', icon: ICONS.target,
      children: [
        { key: 'repeated_attempts_mine', label: '3+ (mine)',
          desc: 'Contacts you\'ve dialled 3+ times that still haven\'t sold.' },
        { key: 'repeated_attempts_all',  label: '1+ (all staff)',
          desc: 'Contacts dialled 1+ times across every staff member, still unsold.' }
      ]
    },
    { key: 'na_common_pool',       label: 'Not-attended pool', desc: 'Numbers nobody picked up — round-robin pool.', tint: '#eff6ff', icon: ICONS.pool },
    { key: 'aditional_followups',  label: 'Extra follow-ups',  desc: 'Stale leads from across your region — both active and resigned staff.', tint: '#ede9fe', icon: ICONS.layers },
    { key: 'own_feedbacks',        label: 'Feedback (mine)',   desc: 'Your delivered orders awaiting feedback.', tint: '#dcfce7', icon: ICONS.inbox },
    { key: 'additional_feedbacks', label: 'Feedback (extra)',  desc: 'Delivered orders from across your region — both active and resigned staff.', tint: '#fce7f3', icon: ICONS.star },
    { key: 'fresh_leads',          label: 'Fresh leads',       desc: 'New numbers from the shared MAP + MVP lead pool.', tint: '#e0f2fe', icon: ICONS.spark },
    { key: 'returns',              label: 'Returns',           desc: 'Orders that came back as returns.', tint: '#fee2e2', icon: ICONS.refresh },
    { key: 'bulk_priority',        label: 'Bulk priority',     desc: 'Shared MAP + MDI + MVP pool: discounted OR multi-item orders (3+ items for MAP, 2+ for MDI/MVP) whose contact has NOT been called by any staff within the cooldown. Each list targets ~33% from each org — topped up to a full list when a pool runs short — with 80% reserved for your own state and 20% country-wide.', tint: '#fef3c7', icon: ICONS.star },
    { key: 'myth_breakers',        label: 'Myth breakers',     desc: 'Calls that need extra effort — Low Priority, Not Interested, Grade 1, Grade 2. Distributed across the active roster.', tint: '#fef3c7', icon: ICONS.flame },
    // Parent grouping with no pipeline of its own — clicking it lands on
    // the first child. activeTab is always one of the leaf keys, so all
    // existing rows/search/description logic keeps working unchanged.
    { key: 'mvp_leads', label: 'MVP leads', desc: 'MVP customers recycled live to MAP staff.', tint: '#fef9c3', icon: ICONS.layers,
      children: [
        { key: 'mvp_leads_followups', label: 'Additional follow-ups', desc: 'MVP calls (not sold). Distributed by phone across active MAP regions.' },
        { key: 'mvp_leads_feedback',  label: 'Additional feedbacks',  desc: 'MVP paid orders with no recent call. Distributed by phone across active MAP regions.' }
      ]
    }
  ],
  2: [
    { key: 'own_followups',      label: 'My follow-ups',     desc: 'Your own contacts due for the next call.', tint: '#ecfdf5', icon: ICONS.phone },
    { key: 'hp_followups',       label: 'High priority',     desc: 'Your own recent call-again-high / not-attended entries.', tint: '#fef3c7', icon: ICONS.flame },
    // Parent grouping — see MAP comment above.
    { key: 'repeated_attempts', label: 'Repeated attempts',
      desc: 'Contacts dialled 3+ times that still haven\'t sold — push hard, escalate, or unlock a special offer.',
      tint: '#ffedd5', icon: ICONS.target,
      children: [
        { key: 'repeated_attempts_mine', label: '3+ (mine)',
          desc: 'Contacts you\'ve dialled 3+ times that still haven\'t sold.' },
        { key: 'repeated_attempts_all',  label: '1+ (all staff)',
          desc: 'Contacts dialled 1+ times across every staff member, still unsold.' }
      ]
    },
    { key: 'priority_followups', label: 'Priority follow-ups', desc: 'Stale region leads with sold contacts excluded.', tint: '#ede9fe', icon: ICONS.layers },
    { key: 'doctors_followups',  label: 'Doctor follow-ups', desc: 'Stale doctor contacts from across your region — active and resigned staff.', tint: '#dcfce7', icon: ICONS.inbox },
    { key: 'na_common_pool',     label: 'Not-attended pool', desc: 'Numbers nobody picked up — round-robin pool.', tint: '#eff6ff', icon: ICONS.pool },
    { key: 'bulk_priority',      label: 'Bulk priority',     desc: 'Shared MAP + MDI + MVP pool: discounted OR multi-item orders (3+ items for MAP, 2+ for MDI/MVP) whose contact has NOT been called by any staff within the cooldown. Each list targets ~33% from each org — topped up to a full list when a pool runs short — with 80% reserved for your own state and 20% country-wide.', tint: '#fef3c7', icon: ICONS.star },
    { key: 'myth_breakers',      label: 'Myth breakers',     desc: 'Calls that need extra effort — Low Priority, Not Interested, Grade 1, Grade 2. Distributed across the active roster.', tint: '#fef3c7', icon: ICONS.flame },
    // Parent grouping with no pipeline of its own — clicking it lands on
    // the first child. Mirrors MAP's "MVP leads" and MVP's "MAP leads"
    // parents; lets MDI staff work MAP's stale leads alongside their own.
    { key: 'map_leads', label: 'MAP leads', desc: 'MAP customers recycled to MDI staff.', tint: '#fef9c3', icon: ICONS.layers,
      children: [
        { key: 'map_leads_bulk_priority', label: 'Bulk priority',         desc: 'Discounted MAP orders. From the last 160 days.' },
        { key: 'map_leads_followups',     label: 'Additional follow-ups', desc: 'MAP calls (not sold). Distributed by phone across active MDI regions.' },
        { key: 'map_leads_feedback',      label: 'Additional feedbacks',  desc: 'MAP paid orders with no recent call. Older than 15 days.' }
      ]
    }
  ],
  // MVP — note feedback keys are SINGULAR ('own_feedback', 'additional_feedback')
  // because that's what mvpCallsAllForView returns. The legacy MVP page
  // (/mvpcalls Blade view) consumes the same response, so renaming the
  // keys would break it. map_leads_* are cron-generated from MAP;
  // fresh_leads is the unified shared pool across MAP + MVP.
  4: [
    { key: 'own_followups',        label: 'My follow-ups',         desc: 'Your own contacts due for the next call.', tint: '#ecfdf5', icon: ICONS.phone },
    { key: 'hp_followups',         label: 'High priority',         desc: 'Recent payment-pending / call-again-high / not-attended entries.', tint: '#fef3c7', icon: ICONS.flame },
    // Parent grouping — see MAP comment above.
    { key: 'repeated_attempts', label: 'Repeated attempts',
      desc: 'Contacts dialled 3+ times that still haven\'t sold — push hard, escalate, or unlock a special offer.',
      tint: '#ffedd5', icon: ICONS.target,
      children: [
        { key: 'repeated_attempts_mine', label: '3+ (mine)',
          desc: 'Contacts you\'ve dialled 3+ times that still haven\'t sold.' },
        { key: 'repeated_attempts_all',  label: '1+ (all staff)',
          desc: 'Contacts dialled 1+ times across every staff member, still unsold.' }
      ]
    },
    { key: 'na_common_pool',       label: 'Not-attended pool',     desc: 'Numbers nobody picked up — round-robin pool.', tint: '#eff6ff', icon: ICONS.pool },
    { key: 'additional_followups', label: 'Extra follow-ups',      desc: 'Stale calls across the active roster.', tint: '#ede9fe', icon: ICONS.layers },
    { key: 'own_feedback',         label: 'Feedback (mine)',       desc: 'Your delivered orders awaiting feedback.', tint: '#dcfce7', icon: ICONS.inbox },
    { key: 'additional_feedback',  label: 'Feedback (extra)',      desc: 'Delivered orders pooled across the active roster.', tint: '#fce7f3', icon: ICONS.star },
    { key: 'fresh_leads',          label: 'Fresh leads',           desc: 'New numbers from the shared MAP + MVP lead pool.', tint: '#e0f2fe', icon: ICONS.spark },
    { key: 'bulk_priority',        label: 'Bulk priority',         desc: 'Shared MAP + MDI + MVP pool: discounted OR multi-item orders (3+ items for MAP, 2+ for MDI/MVP) whose contact has NOT been called by any staff within the cooldown. Each list targets ~33% from each org — topped up to a full list when a pool runs short — with 80% reserved for your own state and 20% country-wide.', tint: '#fef3c7', icon: ICONS.star },
    { key: 'myth_breakers',        label: 'Myth breakers',         desc: 'Calls that need extra effort — Low Priority, Not Interested, Grade 1, Grade 2. Distributed across the active roster.', tint: '#fef3c7', icon: ICONS.flame },
    // Parent grouping with no pipeline of its own — clicking it lands on
    // the first child. Mirror of MAP's "MVP leads" parent on the inverse
    // cross-org pipeline.
    { key: 'map_leads', label: 'MAP leads', desc: 'MAP customers recycled to MVP staff.', tint: '#fef9c3', icon: ICONS.layers,
      children: [
        { key: 'map_leads_followups', label: 'Additional follow-ups', desc: 'MAP calls (not sold). Distributed by phone across active MVP regions.' },
        { key: 'map_leads_feedback',  label: 'Additional feedbacks',  desc: 'MAP paid orders with no recent call. Distributed by phone across active MVP regions.' }
      ]
    }
  ]
}

// Per-org map: tab key → admin-tunable day-setting key + suffix template.
// Used by descForTab() to splice "From the last N days" / "Calls older
// than N days" / etc. into the tab description, reflecting whatever the
// admin set in Settings → Call Queues.
const TAB_DAY_DESCRIPTORS_BY_ORG = {
  1: {
    own_followups:        { key: 'CALL_QUEUE_DAYS_OLD',           tpl: ' Calls older than {n} days.' },
    hp_followups:         { key: 'CALL_QUEUE_HP_DAYS_OLD',        tpl: ' From the last {n} days.' },
    na_common_pool:       { key: 'NA_COMMON_POOL_DAYS_OLD',       tpl: ' From the last {n} days.' },
    aditional_followups:  { key: 'ADDITIONAL_FOLLOWUPS_DAYS_OLD', tpl: ' Calls older than {n} days.' },
    own_feedbacks:        { key: 'FEEDBACK_CALLS_DAYS_OLD',       tpl: ' Orders older than {n} days.' },
    additional_feedbacks: { key: 'FEEDBACK_CALLS_DAYS_OLD',       tpl: ' Orders older than {n} days.' },
    mvp_leads_followups:  { key: 'MAP_MVP_LEADS_DAYS_OLD',        tpl: ' Calls older than {n} days.' },
    mvp_leads_feedback:   { key: 'MAP_MVP_LEADS_DAYS_OLD',        tpl: ' Orders older than {n} days.' },
    bulk_priority:        { key: 'MAP_BULK_PRIORITY_DAYS_OLD',    tpl: ' Orders older than {n} days.' },
    myth_breakers:        { key: 'MAP_MYTH_BREAKERS_DAYS_OLD',    tpl: ' From the last {n} days.' },
    repeated_attempts_mine: { key: 'MAP_REPEATED_ATTEMPTS_DAYS_OLD',     tpl: ' Counting attempts from the last {n} days.' },
    repeated_attempts_all:  { key: 'MAP_REPEATED_ATTEMPTS_ALL_DAYS_OLD', tpl: ' Counting attempts from the last {n} days.' }
  },
  2: {
    own_followups:      { key: 'MDI_OWN_DAYS_OLD',      tpl: ' Calls older than {n} days.' },
    hp_followups:       { key: 'MDI_HP_DAYS_OLD',       tpl: ' From the last {n} days.' },
    priority_followups: { key: 'MDI_PRIORITY_DAYS_OLD', tpl: ' Calls older than {n} days.' },
    doctors_followups:  { key: 'MDI_DOCTOR_DAYS_OLD',   tpl: ' Calls older than {n} days.' },
    na_common_pool:     { key: 'MDI_NA_DAYS_OLD',       tpl: ' From the last {n} days.' },
    myth_breakers:      { key: 'MDI_MYTH_BREAKERS_DAYS_OLD', tpl: ' From the last {n} days.' },
    repeated_attempts_mine: { key: 'MDI_REPEATED_ATTEMPTS_DAYS_OLD',     tpl: ' Counting attempts from the last {n} days.' },
    repeated_attempts_all:  { key: 'MDI_REPEATED_ATTEMPTS_ALL_DAYS_OLD', tpl: ' Counting attempts from the last {n} days.' }
  },
  4: {
    own_followups:        { key: 'MVP_OWN_DAYS_OLD',                 tpl: ' Calls older than {n} days.' },
    hp_followups:         { key: 'MVP_HP_DAYS_OLD',                  tpl: ' From the last {n} days.' },
    na_common_pool:       { key: 'MVP_NA_DAYS_OLD',                  tpl: ' From the last {n} days.' },
    additional_followups: { key: 'MVP_ADDITIONAL_DAYS_OLD',          tpl: ' Calls older than {n} days.' },
    own_feedback:         { key: 'MVP_OWN_FEEDBACK_DAYS_OLD',        tpl: ' Orders older than {n} days.' },
    additional_feedback:  { key: 'MVP_ADDITIONAL_FEEDBACK_DAYS_OLD', tpl: ' Orders older than {n} days.' },
    map_leads_followups:  { key: 'MVP_MAP_LEADS_DAYS_OLD',           tpl: ' Calls older than {n} days.' },
    map_leads_feedback:   { key: 'MVP_MAP_LEADS_DAYS_OLD',           tpl: ' Orders older than {n} days.' },
    myth_breakers:        { key: 'MVP_MYTH_BREAKERS_DAYS_OLD',       tpl: ' From the last {n} days.' },
    repeated_attempts_mine: { key: 'MVP_REPEATED_ATTEMPTS_DAYS_OLD',     tpl: ' Counting attempts from the last {n} days.' },
    repeated_attempts_all:  { key: 'MVP_REPEATED_ATTEMPTS_ALL_DAYS_OLD', tpl: ' Counting attempts from the last {n} days.' }
  }
}

const AVATAR_PALETTE = [
  ['#0e5b46', '#16a07a'],
  ['#1e3a8a', '#3b82f6'],
  ['#5b21b6', '#8b5cf6'],
  ['#b45309', '#f59e0b'],
  ['#115e59', '#14b8a6'],
  ['#7f1d1d', '#ef4444']
]

function statusBadgeClass (status) {
  const s = String(status || '').toLowerCase().replace(/-/g, '_')
  if (s.includes('sold')) return 'cq-row__badge--green'
  if (s.includes('payment_pending')) return 'cq-row__badge--amber'
  if (s.includes('call_again_high') || s.includes('not_attended')) return 'cq-row__badge--red'
  if (s.includes('call_again_low')) return 'cq-row__badge--blue'
  if (s.includes('not_interested') || s.includes('disconnected')) return 'cq-row__badge--gray'
  if (s.includes('feedback')) return 'cq-row__badge--violet'
  return 'cq-row__badge--gray'
}

// Heat scale for the Repeated-attempts tab. The badge text is the
// raw attempt count, but the colour escalates so callers can spot
// "this customer is way past the usual 3-strike line" at a glance:
// amber 3-4 (just qualifying), red 5-6 (genuinely persistent), violet
// 7+ (escalate / unlock an offer / hand to a manager).
function attemptsBadgeClass (attempts) {
  const n = Number(attempts) || 0
  if (n >= 7) return 'cq-row__badge--violet'
  if (n >= 5) return 'cq-row__badge--red'
  return 'cq-row__badge--amber'
}

function prettyStatus (status) {
  return String(status || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim()
}

// Matches the responsive breakpoint used in this page's CSS (see the
// `@media (max-width: 767px)` block at the bottom). Kept as a const so the
// JS query and the CSS rule can't drift.
const MOBILE_MEDIA_QUERY = '(max-width: 767px)'

// Skip refetch on keep-alive re-activation if the data was fetched within
// this window. Long enough to absorb rapid back-and-forth navigation;
// short enough that cron-generated changes show up within a minute.
const FRESH_TTL_MS = 60_000

// localStorage key for the "I've already copied this phone" set.
// Persisted across reloads so staff can track which numbers they've
// already dialled across a long working session. Stored as a JSON
// array of 10-digit phone strings.
const COPIED_PHONES_KEY = 'cq-copied-phones-v1'
const COPIED_PHONES_MAX = 5000 // cap so the array can't grow forever

// localStorage key for the "I've already dialled this number" set.
// Populated whenever the user hits Call (desktop button → /telesales, or
// the mobile tel: link). Persisted so the whole row stays highlighted as
// a "you've called this customer" marker across reloads and revisits.
const CALLED_PHONES_KEY = 'cq-called-phones-v1'
const CALLED_PHONES_MAX = 5000

export default {
  name: 'CallQueue',
  components: { CustomerHistoryModal },
  data () {
    return {
      isLoading: false,
      isRefreshing: false,
      errorMessage: '',
      activeTab: 'own_followups',
      searchTerm: '',
      lastFetchedAt: null,
      data: {},
      lastGenerated: {},
      counts: {},
      formData: {},
      daySettings: {},
      isMobile: false,
      _mobileMql: null,
      _mobileListener: null,
      // Phones the user has clicked "Copy" on. Populated from
      // localStorage in created(); persisted on every copy so the
      // "Copied" state survives page reloads and switches between
      // organisations. Plain array (not Set) for Vue 2 reactivity.
      copiedPhones: [],
      // Phones the user has hit "Call" on. Drives the whole-row "Called"
      // highlight so staff can see at a glance who they've already dialled.
      // Persisted to localStorage like copiedPhones.
      calledPhones: [],
      // View-history modal state. The modal lazily fetches calls + orders
      // for the selected customer's phone (same endpoint as TeleSales).
      historyModalShow: false,
      historyPhone: '',
      historyName: ''
    }
  },
  computed: {
    activeOrgId () {
      return Number(
        this.$store?.state?.activeOrganisationId
        || this.authUser()?.organisation_id
        || 0
      ) || 0
    },
    supportedOrg () {
      return !!ENDPOINTS[this.activeOrgId]
    },
    orgLabel () {
      return ORG_LABELS[this.activeOrgId] || ''
    },
    greetingName () {
      return this.formData?.name || this.authUser()?.name || 'there'
    },
    // Tab list for the current org. MAP and MDI have different pipeline
    // sets, so we never iterate a static const — always derive from the
    // active org. An unsupported org (e.g., MVP today) gets [].
    tabDefs () {
      return TAB_DEFS_BY_ORG[this.activeOrgId] || []
    },
    // Flatten parent.children → leaves. Every existing consumer that needs
    // to operate on a real pipeline key (loadData, descForTab, normalizeRows,
    // search, generated-at) iterates this list — parents are UI-only.
    tabLeafDefs () {
      return this.tabDefs.reduce((acc, tab) => {
        if (Array.isArray(tab.children) && tab.children.length) {
          tab.children.forEach((child) => acc.push(child))
        } else {
          acc.push(tab)
        }
        return acc
      }, [])
    },
    // Top-level rendering: parents collapse to a single tile/pill whose
    // count is the sum of their children. activeTab is always a leaf, so
    // we mark a parent active when any of its children matches.
    tabsForRender () {
      return this.tabDefs.map((tab) => {
        if (Array.isArray(tab.children) && tab.children.length) {
          const childCount = tab.children.reduce((sum, c) => sum + this.countFor(c.key), 0)
          return { ...tab, count: childCount, isParent: true }
        }
        return { ...tab, count: this.countFor(tab.key), isParent: false }
      })
    },
    activeParentTab () {
      return this.tabDefs.find((t) =>
        Array.isArray(t.children) && t.children.some((c) => c.key === this.activeTab)
      ) || null
    },
    childTabsForRender () {
      if (!this.activeParentTab) return []
      return this.activeParentTab.children.map((c) => ({
        ...c,
        count: this.countFor(c.key)
      }))
    },
    activeTabMeta () {
      const defs = this.tabLeafDefs
      const found = defs.find((t) => t.key === this.activeTab) || defs[0] || { key: '', label: '', desc: '', icon: '' }
      return { ...found, description: this.descForTab(found) }
    },
    activeRows () {
      return this.normalizeRows(this.activeTab, this.data[this.activeTab])
    },
    filteredRows () {
      const term = this.searchTerm.toLowerCase()
      if (!term) return this.activeRows
      return this.activeRows.filter((row) => {
        return [row.name, row.phone, row.altPhone, row.subtitle, row.detail, row.badge]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term))
      })
    },
    lastFetchedLabel () {
      if (!this.lastFetchedAt) return ''
      const h = String(this.lastFetchedAt.getHours()).padStart(2, '0')
      const m = String(this.lastFetchedAt.getMinutes()).padStart(2, '0')
      return `${h}:${m}`
    },
    isBusy () {
      return this.isLoading || this.isRefreshing
    }
  },
  watch: {
    activeOrgId () {
      // Tab keys differ across orgs (MAP has returns, MDI has doctors_followups,
      // etc.) so cached rows from another org must not bleed through. Reset to
      // the cold-load path before fetching.
      this.data = {}
      this.counts = {}
      this.lastGenerated = {}
      this.lastFetchedAt = null
      this.loadData()
    },
    activeTab () { this.searchTerm = '' }
  },
  created () {
    this._loadedOnMount = true
    this.copiedPhones = this.loadCopiedPhones()
    this.calledPhones = this.loadCalledPhones()
    this.loadData()
  },
  // Inside <keep-alive>, so created() only fires once. activated() runs on
  // every visit; on revisits do a stale-while-revalidate refresh (cached
  // rows stay on screen, network call happens silently) and skip entirely
  // if the data is fresher than FRESH_TTL_MS — that's what makes rapid
  // back-and-forth navigation feel instant.
  activated () {
    if (this._loadedOnMount) {
      this._loadedOnMount = false
      return
    }
    if (this.isWithinTtl()) return
    this.loadData()
  },
  mounted () {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia(MOBILE_MEDIA_QUERY)
    this.isMobile = mql.matches
    const listener = (event) => { this.isMobile = event.matches }
    mql.addEventListener('change', listener)
    this._mobileMql = mql
    this._mobileListener = listener
  },
  beforeDestroy () {
    if (this._mobileMql && this._mobileListener) {
      this._mobileMql.removeEventListener('change', this._mobileListener)
    }
    this._mobileMql = null
    this._mobileListener = null
  },
  methods: {
    authUser () {
      try {
        const raw = localStorage.getItem('auth_user')
        return raw ? JSON.parse(raw) : null
      } catch (e) {
        return null
      }
    },
    authToken () {
      return localStorage.getItem('api_token')
    },
    isWithinTtl () {
      return this.lastFetchedAt && (Date.now() - this.lastFetchedAt.getTime() < FRESH_TTL_MS)
    },
    refresh () {
      // The toolbar Refresh button bypasses the TTL — staff want to force a
      // pull when they hit it. UI stays populated (isRefreshing, not
      // isLoading) so rows don't flash to a spinner.
      this.lastFetchedAt = null
      this.loadData()
    },
    // Click handler for the top-level tile/pill. Parents have no rows of
    // their own — route to the first non-empty child, or the first child
    // if none have data.
    selectTopLevelTab (tab) {
      if (Array.isArray(tab.children) && tab.children.length) {
        const target = tab.children.find((c) => this.countFor(c.key)) || tab.children[0]
        this.activeTab = target.key
        return
      }
      this.activeTab = tab.key
    },
    loadData () {
      const endpoint = ENDPOINTS[this.activeOrgId]
      if (!endpoint) return
      // Cold load (no rows yet) blocks the body with the spinner. With
      // cached rows in hand we refresh silently so the table never blanks.
      const hasCachedRows = !!this.lastFetchedAt
      if (hasCachedRows) {
        this.isRefreshing = true
      } else {
        this.isLoading = true
      }
      this.errorMessage = ''
      axios.get(`${this.$apiAdress}${endpoint}?token=${this.authToken()}`)
        .then((response) => {
          const body = response.data || {}
          // Shape varies per org (MAP returns 8 arrays, MDI returns 5).
          // Pull each leaf tab's slice from the response by key — orgs
          // that don't have a particular pipeline simply yield []. Iterate
          // leaves, not parents: parents are UI-only and have no payload.
          const data = {}
          this.tabLeafDefs.forEach((tab) => {
            data[tab.key] = Array.isArray(body[tab.key]) ? body[tab.key] : []
          })
          this.data = data
          this.lastGenerated = body.last_generated || {}
          this.counts = body.counts || {}
          this.formData = body.form_data || {}
          this.daySettings = body.day_settings || {}
          this.lastFetchedAt = new Date()
          // If the user lands with an empty default tab, jump to the first
          // non-empty one so they don't see a blank page. Only do this on
          // the cold load — on a silent refresh we keep the user's chosen tab.
          if (!hasCachedRows && !this.countFor(this.activeTab)) {
            const firstNonEmpty = this.tabLeafDefs.find((t) => this.countFor(t.key))
            if (firstNonEmpty) this.activeTab = firstNonEmpty.key
          }
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load your call queue right now.'
        })
        .finally(() => {
          this.isLoading = false
          this.isRefreshing = false
        })
    },
    descForTab (tab) {
      // Append the admin-tunable day count to the description so tele
      // staff see exactly how stale "stale" means right now. The lookup
      // is keyed (org, tab) so MAP and MDI use their own day-setting
      // names — see TAB_DAY_DESCRIPTORS_BY_ORG.
      const baseDesc = tab.desc || ''
      const orgMap = TAB_DAY_DESCRIPTORS_BY_ORG[this.activeOrgId] || {}
      const descriptor = orgMap[tab.key]
      if (!descriptor) return baseDesc

      const days = (this.daySettings || {})[descriptor.key]
      if (!days) return baseDesc

      return baseDesc + descriptor.tpl.replace('{n}', String(days))
    },
    countFor (key) {
      const fromCounts = this.counts && (key in this.counts ? this.counts[key] : null)
      if (typeof fromCounts === 'number') return fromCounts
      const arr = this.data[key]
      return Array.isArray(arr) ? arr.length : 0
    },
    lastGeneratedFor (key) {
      // The API uses 'additional_followups' in last_generated but the data
      // payload uses 'aditional_followups' (typo preserved from server).
      const lookup = key === 'aditional_followups' ? 'additional_followups' : key
      return this.lastGenerated && this.lastGenerated[lookup]
    },
    normalizeRows (key, rows) {
      if (!Array.isArray(rows)) return []
      switch (key) {
        case 'own_followups':
        case 'hp_followups':
        case 'na_common_pool':
        case 'aditional_followups':
        // MDI-specific call-shaped pipelines — same row format as MAP's
        // call followups (contact_name / contact_phone / call_type /
        // comments / status), so they normalise via the same arm.
        case 'doctors_followups':
        case 'priority_followups':
        // Call-shaped cross-org pipelines — additional_followups,
        // map_leads_followups (MAP leads recycled to MVP) and
        // mvp_leads_followups (the inverse: MVP leads recycled to MAP)
        // all share this shape.
        case 'additional_followups':
        case 'map_leads_followups':
        case 'mvp_leads_followups':
        case 'myth_breakers':
          return rows.map((r, i) => ({
            _id: r.id || `${r.contact_id || ''}-${i}`,
            name: r.contact_name,
            phone: this.normalizePhone(r.contact_phone),
            // Stamped server-side by AlternatePhoneEnricher when the
            // customer's address row or linked order carries a second
            // number distinct from the primary.
            altPhone: r.alternate_phone ? this.normalizePhone(r.alternate_phone) : '',
            subtitle: r.call_type ? prettyStatus(r.call_type) : '',
            detail: r.comments || '',
            badge: r.status ? prettyStatus(r.status) : '',
            badgeClass: statusBadgeClass(r.status)
          }))
        case 'own_feedbacks':
        case 'additional_feedbacks':
        // MVP uses singular keys for the same order-shaped feedback rows.
        // The legacy /mvpcalls Blade view consumes these names so we
        // can't rename without breaking that page.
        case 'own_feedback':
        case 'additional_feedback':
        case 'map_leads_feedback':
        case 'mvp_leads_feedback':
        case 'bulk_priority':
        case 'map_leads_bulk_priority':
          return rows.map((r, i) => ({
            _id: r.order_id || r.id || `${r.contact_phone || ''}-${i}`,
            name: r.contact_name,
            phone: this.normalizePhone(r.contact_phone),
            altPhone: r.alternate_phone ? this.normalizePhone(r.alternate_phone) : '',
            subtitle: r.products ? r.products : (r.delivery_date ? `Delivered ${this.formatDate(r.delivery_date)}` : ''),
            detail: r.contact_address || '',
            amount: r.total_amount || 0,
            badge: r.payment_status ? prettyStatus(r.payment_status) : '',
            badgeClass: statusBadgeClass(r.payment_status)
          }))
        case 'fresh_leads':
          return rows.map((r, i) => ({
            _id: r.id || i,
            name: r.name,
            phone: this.normalizePhone(r.phone),
            // Surfaced as a second tel: link in the row. The importer
            // already filters out alt == primary, so a populated value
            // here is genuinely a different number.
            altPhone: r.alternate_phone ? this.normalizePhone(r.alternate_phone) : '',
            subtitle: [r.place, r.district].filter(Boolean).join(' · '),
            detail: r.address || '',
            badge: r.category ? prettyStatus(r.category) : '',
            badgeClass: 'cq-row__badge--blue'
          }))
        case 'returns':
          return rows.map((r, i) => ({
            _id: r.id || i,
            name: r.contact_name,
            phone: this.normalizePhone(r.contact_phone),
            altPhone: r.alternate_phone ? this.normalizePhone(r.alternate_phone) : '',
            subtitle: r.return_date ? `Returned ${this.formatDate(r.return_date)}` : '',
            detail: r.products || r.contact_address || '',
            amount: r.total_amount || 0,
            badge: r.payment_status ? prettyStatus(r.payment_status) : 'Return',
            badgeClass: 'cq-row__badge--red'
          }))
        // Repeated-attempts rows. Two sources, same shape:
        //   - `repeated_attempts_mine` → *_repeated_attempts snapshot
        //     (per-user, ≥ 3 attempts, populated by
        //     generateRepeatedAttempts() via Settings → Run now).
        //   - `repeated_attempts_all` → live RepeatedAttemptsService
        //     ::forOrganisation (every caller in the org, ≥ 1 attempt).
        // Both carry `attempts` and `call_created_at` (latest call) on
        // top of the usual call-row shape. The attempt count is what
        // makes this tab worth visiting, so it takes the badge slot
        // (colour escalates 3-4 → 5-6 → 7+); the most-recent call's
        // status / call-type / recency lives in the subtitle so the
        // caller knows what was tried last time before dialling again.
        // For the snapshot variant, `created_at` here is the snapshot
        // time, surfaced separately as "Generated · X ago" by the card
        // head; the live variant has no such snapshot timestamp.
        case 'repeated_attempts_mine':
        case 'repeated_attempts_all':
          return rows.map((r, i) => {
            const attempts = Number(r.attempts) || 0
            const lastOutcome = r.call_type ? prettyStatus(r.call_type) : (r.status ? prettyStatus(r.status) : '')
            const lastCallAt = r.call_created_at || r.created_at
            const relativeLast = lastCallAt ? this.formatRelative(lastCallAt) : ''
            const subtitleParts = [lastOutcome, relativeLast ? `Last call ${relativeLast}` : ''].filter(Boolean)
            return {
              _id: r.id || `${r.contact_id || ''}-${i}`,
              name: r.contact_name,
              phone: this.normalizePhone(r.contact_phone),
              altPhone: r.alternate_phone ? this.normalizePhone(r.alternate_phone) : '',
              subtitle: subtitleParts.join(' · '),
              detail: r.comments || '',
              badge: `${attempts} attempts`,
              badgeClass: attemptsBadgeClass(attempts)
            }
          })
        default:
          return []
      }
    },
    normalizePhone (raw) {
      if (raw === null || raw === undefined) return ''
      const digits = String(raw).replace(/\D/g, '')
      if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2)
      if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1)
      return digits.slice(-10)
    },
    callContact (row) {
      if (!row || !row.phone) return
      this.markCalled(row.phone)
      this.$router.push({ path: '/telesales', query: { phone: row.phone } })
    },
    openHistory (row) {
      if (!row || !row.phone) return
      this.historyPhone = row.phone
      this.historyName = row.name || ''
      this.historyModalShow = true
    },
    // ---------- Copy-phone state ----------
    loadCopiedPhones () {
      try {
        const raw = localStorage.getItem(COPIED_PHONES_KEY)
        if (!raw) return []
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed.map((p) => String(p)) : []
      } catch (e) {
        return []
      }
    },
    persistCopiedPhones () {
      try {
        // Trim to the most-recent N so the array can't grow forever.
        if (this.copiedPhones.length > COPIED_PHONES_MAX) {
          this.copiedPhones = this.copiedPhones.slice(-COPIED_PHONES_MAX)
        }
        localStorage.setItem(COPIED_PHONES_KEY, JSON.stringify(this.copiedPhones))
      } catch (e) {
        // localStorage full / disabled / private mode — silent.
      }
    },
    isPhoneCopied (phone) {
      if (!phone) return false
      return this.copiedPhones.indexOf(String(phone)) !== -1
    },
    copyPhone (phone) {
      if (!phone) return
      const text = String(phone)
      const markCopied = () => {
        if (this.copiedPhones.indexOf(text) === -1) {
          this.copiedPhones.push(text)
          this.persistCopiedPhones()
        }
      }
      // Modern Clipboard API requires a secure context (https or
      // localhost). Fall back to the textarea + execCommand trick on
      // plain HTTP / older browsers so the button still works.
      const fallback = () => {
        try {
          const ta = document.createElement('textarea')
          ta.value = text
          ta.setAttribute('readonly', '')
          ta.style.position = 'fixed'
          ta.style.top = '-9999px'
          document.body.appendChild(ta)
          ta.select()
          ta.setSelectionRange(0, text.length)
          const ok = document.execCommand('copy')
          document.body.removeChild(ta)
          if (ok) markCopied()
        } catch (e) { /* clipboard unavailable */ }
      }
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(markCopied).catch(fallback)
      } else {
        fallback()
      }
    },
    // ---------- Called-phone state ----------
    loadCalledPhones () {
      try {
        const raw = localStorage.getItem(CALLED_PHONES_KEY)
        if (!raw) return []
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed.map((p) => String(p)) : []
      } catch (e) {
        return []
      }
    },
    persistCalledPhones () {
      try {
        if (this.calledPhones.length > CALLED_PHONES_MAX) {
          this.calledPhones = this.calledPhones.slice(-CALLED_PHONES_MAX)
        }
        localStorage.setItem(CALLED_PHONES_KEY, JSON.stringify(this.calledPhones))
      } catch (e) {
        // localStorage full / disabled / private mode — silent.
      }
    },
    isPhoneCalled (phone) {
      if (!phone) return false
      return this.calledPhones.indexOf(String(phone)) !== -1
    },
    markCalled (phone) {
      if (!phone) return
      const text = String(phone)
      if (this.calledPhones.indexOf(text) === -1) {
        this.calledPhones.push(text)
        this.persistCalledPhones()
      }
    },
    initials (text) {
      const parts = String(text || '').trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '?'
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    },
    avatarStyle (text) {
      const seed = String(text || '?')
      let hash = 0
      for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
      const [from, to] = AVATAR_PALETTE[hash % AVATAR_PALETTE.length]
      return { background: `linear-gradient(135deg, ${from}, ${to})` }
    },
    formatNumber (value) {
      const numeric = Number(value || 0)
      if (!Number.isFinite(numeric)) return '0'
      return numeric.toLocaleString('en-IN', { maximumFractionDigits: 2 })
    },
    formatDate (raw) {
      if (!raw) return ''
      const d = new Date(String(raw).replace(' ', 'T'))
      if (Number.isNaN(d.getTime())) return String(raw)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${d.getDate()} ${months[d.getMonth()]}`
    },
    formatRelative (raw) {
      if (!raw) return ''
      const d = new Date(String(raw).replace(' ', 'T'))
      if (Number.isNaN(d.getTime())) return String(raw)
      const diffMs = Date.now() - d.getTime()
      const mins = Math.round(diffMs / 60000)
      if (mins < 1) return 'just now'
      if (mins < 60) return `${mins} min ago`
      const hours = Math.round(mins / 60)
      if (hours < 24) return `${hours} hr ago`
      const days = Math.round(hours / 24)
      if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`
      return this.formatDate(raw)
    }
  }
}
</script>

<style scoped>
.cq-page {
  padding: 0.25rem 0;
}

/* ── Toolbar ── */
.cq-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.cq-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex: 1 1 auto;
  min-width: 0;
}

.cq-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.cq-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.cq-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.cq-toolbar__title strong {
  color: #0e5b46;
}

.cq-toolbar__org {
  display: inline-block;
  margin-left: 0.5rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #4b5563;
  background: #f1f5f9;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
}

.cq-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-shrink: 0;
}

.cq-updated {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.cq-toolbar__actions >>> .btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.cq-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.refresh-btn__icon {
  flex-shrink: 0;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.cq-toolbar__actions >>> .btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: cq-spin 0.9s linear infinite;
}

@keyframes cq-spin { to { transform: rotate(360deg); } }

.cq-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* ── Unsupported org ── */
.cq-unsupported {
  background: #ffffff;
  border: 1px dashed #e5e9f2;
  border-radius: 12px;
  padding: 2rem 1rem;
  text-align: center;
}

.cq-unsupported__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
}

.cq-unsupported__hint {
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.3rem;
}

/* ── Summary tiles ── */
.cq-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.6rem;
  margin-bottom: 1rem;
}

.cq-summary__tile {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.65rem 0.85rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.cq-summary__tile:hover,
.cq-summary__tile:focus-visible {
  border-color: #c7ebda;
  box-shadow: 0 4px 12px rgba(14, 91, 70, 0.1);
  transform: translateY(-1px);
}

.cq-summary__tile.is-active {
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.16);
}

.cq-summary__tile.is-empty {
  opacity: 0.7;
}

.cq-summary__icon {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #0e5b46;
  flex-shrink: 0;
}

.cq-summary__body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.cq-summary__label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #4b5563;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cq-summary__count {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
}

/* ── Tabs ── */
.cq-tabs {
  display: flex;
  gap: 0.4rem;
  margin-bottom: 0.85rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
  scrollbar-width: thin;
}

.cq-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.85rem;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  background: #ffffff;
  color: #4b5563;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.cq-tab:hover {
  background: #f5fbf8;
  border-color: #c7ebda;
  color: #0e5b46;
}

.cq-tab.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.cq-tab__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: #eef2f7;
  color: #4b5563;
  font-size: 0.68rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.cq-tab.is-active .cq-tab__count {
  background: rgba(255, 255, 255, 0.25);
  color: #ffffff;
}

.cq-tab__count.is-zero {
  opacity: 0.55;
}

/* ── Inner sub-tabs (under a parent pill, e.g. MVP leads) ── */
.cq-subtabs {
  display: flex;
  gap: 0.4rem;
  margin: -0.35rem 0 0.85rem 0.15rem;
  padding: 0.35rem 0.5rem;
  border-left: 3px solid #16a07a;
  background: #f5fbf8;
  border-radius: 0 8px 8px 0;
  overflow-x: auto;
  scrollbar-width: thin;
}

.cq-subtab {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.7rem;
  border: 1px solid #d6efe2;
  border-radius: 999px;
  background: #ffffff;
  color: #4b5563;
  font-size: 0.74rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.cq-subtab:hover {
  background: #ecfdf5;
  border-color: #16a07a;
  color: #0e5b46;
}

.cq-subtab:focus { outline: none; }
.cq-subtab::-moz-focus-inner { border: 0; }
.cq-subtab:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.cq-subtab.is-active {
  background: #0e5b46;
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 2px 5px rgba(14, 91, 70, 0.18);
}

.cq-subtab__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 16px;
  padding: 0 0.3rem;
  border-radius: 999px;
  background: #eef2f7;
  color: #4b5563;
  font-size: 0.64rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.cq-subtab.is-active .cq-subtab__count {
  background: rgba(255, 255, 255, 0.25);
  color: #ffffff;
}

.cq-subtab__count.is-zero {
  opacity: 0.55;
}

/* ── List card ── */
.cq-list-card {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.cq-list-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  gap: 0.85rem;
  flex-wrap: wrap;
}

.cq-list-card__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.cq-list-card__desc {
  margin: 0.15rem 0 0;
  font-size: 0.74rem;
  color: #6b7280;
}

.cq-list-card__tools {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
}

.cq-list-card__generated {
  font-size: 0.72rem;
  color: #6b7280;
  font-weight: 500;
}

.cq-search-wrap {
  position: relative;
  width: 240px;
  max-width: 100%;
}

.cq-search-icon {
  position: absolute;
  top: 50%;
  left: 0.7rem;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.cq-search-input {
  width: 100%;
  height: 32px;
  padding: 0.3rem 2.1rem 0.3rem 2rem;
  border: 1px solid #d8dde6;
  border-radius: 999px;
  background: #ffffff;
  font-size: 0.8rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.cq-search-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.cq-search-clear {
  position: absolute;
  top: 50%;
  right: 0.45rem;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: 0;
  border-radius: 50%;
  background: #e5e9f2;
  color: #4b5563;
  font-size: 0.95rem;
  line-height: 1;
  cursor: pointer;
}

.cq-search-clear:hover {
  background: #d1d5db;
  color: #1f2a44;
}

.cq-list-state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  padding: 2rem 1rem;
  color: #4b5563;
  font-size: 0.85rem;
  font-weight: 500;
}

.cq-list-state--empty {
  background: #fbfcfe;
}

.cq-list-state__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
}

.cq-list-state__hint {
  margin-top: 0.3rem;
  font-size: 0.78rem;
  color: #6b7280;
}

.cq-link-btn {
  border: 0;
  background: transparent;
  color: #0e5b46;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.cq-link-btn:hover { text-decoration: underline; }

/* ── Row list ── */
.cq-row-list {
  list-style: none;
  margin: 0;
  padding: 0.4rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.cq-row {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid transparent;
  border-radius: 10px;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.cq-row:hover {
  background: #f8fafc;
  border-color: #e5e9f2;
}

/* Whole-row marker for customers the user has already dialled. A soft
   green wash + left accent that survives reloads (state in localStorage). */
.cq-row.is-called {
  background: #f0fdf6;
  border-color: #bbf7d0;
  box-shadow: inset 3px 0 0 0 #16a07a;
}

.cq-row.is-called:hover {
  background: #e7fbef;
  border-color: #86efac;
}

.cq-row__called-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  background: #dcfce7;
  color: #0e5b46;
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
}

.cq-row__called-tag svg {
  flex-shrink: 0;
}

.cq-row__avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
}

.cq-row__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.cq-row__line {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.cq-row__line--meta { gap: 0.7rem; }

.cq-row__name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1f2a44;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cq-row__phone {
  font-size: 0.78rem;
  font-weight: 600;
  color: #0e5b46;
  font-variant-numeric: tabular-nums;
  text-decoration: none;
}

.cq-row__phone:hover { text-decoration: underline; }

/* Alternate phone — same hit target as primary but visually secondary
   so staff can still click-to-call but doesn't compete for the eye. */
.cq-row__phone--alt {
  color: #4b5563;
  font-weight: 500;
  font-size: 0.72rem;
  padding: 0.05rem 0.4rem;
  background: #f1f5f9;
  border-radius: 999px;
}

.cq-row__phone--alt:hover { color: #0e5b46; background: #ecfdf5; text-decoration: none; }

/* Inline copy-phone button. Sits immediately after each phone link.
   Subtle by default so it doesn't compete with the tel: link; flips
   to brand-green with a checkmark once the number has been copied
   (state persists across reloads via localStorage). */
.cq-copy-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1rem 0.4rem;
  margin-left: 0.1rem;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #6b7280;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;
}

.cq-copy-btn:hover {
  color: #0e5b46;
  background: #f5fbf8;
  border-color: #c7ebda;
}

.cq-copy-btn.is-copied {
  color: #0e5b46;
  background: #ecfdf5;
  border-color: #c7ebda;
}

.cq-copy-btn:focus { outline: none; }
.cq-copy-btn::-moz-focus-inner { border: 0; }
.cq-copy-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.cq-row__subtitle {
  font-size: 0.74rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60%;
}

.cq-row__detail {
  font-size: 0.74rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cq-row__badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
}

.cq-row__badge--green   { background: #ecfdf5; color: #0e5b46; }
.cq-row__badge--amber   { background: #fef3c7; color: #92400e; }
.cq-row__badge--red     { background: #fee2e2; color: #991b1b; }
.cq-row__badge--blue    { background: #dbeafe; color: #1e40af; }
.cq-row__badge--violet  { background: #ede9fe; color: #5b21b6; }
.cq-row__badge--gray    { background: #f1f5f9; color: #475569; }

.cq-row__actions {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-shrink: 0;
}

.cq-row__amount {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1f2a44;
  font-variant-numeric: tabular-nums;
}

.cq-call-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 30px;
  padding: 0 0.85rem;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(14, 91, 70, 0.18);
  transition: filter 0.15s ease, transform 0.15s ease;
}

.cq-call-btn:hover:not(:disabled) {
  filter: brightness(1.05);
  transform: translateY(-1px);
}

.cq-call-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  filter: saturate(0.5);
}

/* When rendered as an <a tel:…> on mobile the default link styles fight the
   gradient button look — neutralise them so it matches the desktop button. */
.cq-call-btn--link,
.cq-call-btn--link:hover,
.cq-call-btn--link:focus {
  color: #ffffff;
  text-decoration: none;
}

/* Secondary, outlined to sit beside the gradient Call button without
   competing with it. */
.cq-history-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 30px;
  padding: 0 0.7rem;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.cq-history-btn svg {
  color: #16a07a;
  flex-shrink: 0;
}

.cq-history-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.cq-history-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cq-history-btn:focus { outline: none; }
.cq-history-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

@media (max-width: 767px) {
  .cq-toolbar { flex-wrap: wrap; }
  .cq-row { flex-wrap: wrap; }
  .cq-row__actions {
    flex-basis: 100%;
    justify-content: flex-end;
  }
  .cq-search-wrap { width: 100%; }
}
</style>
